import React from 'react';
import AudioPlayer from 'react-h5-audio-player';
import { toSnakeCase } from '../helpers/common';
import DownloadIcon from '../assets/images/audio-download.inline.svg';

const AudioPlayerModule = (props) => {
  const { anchorTagLinkText, heading, transcript, transcriptDownloadFile, introText, audioFile } =
    props.module;
  const [duration, setDuration] = React.useState('');
  const player = React.useRef(null);
  const transcriptBtn = React.useRef(null);
  const transcriptWrapper = React.useRef(null);
  const audioFilePath = audioFile?.mediaItemUrl;

  const formatTime = (seconds) => {
    seconds = Math.floor(seconds);
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;
    let formattedTime = '';
    if (hours > 0) {
      formattedTime += `${String(hours).padStart(2, '0')}:`;
    }
    formattedTime += `${String(minutes).padStart(2, '0')}:${String(
      remainingSeconds
    ).padStart(2, '0')}`;
    return formattedTime;
  };

  const showTranscript = () => {
    if (
      transcriptBtn?.current?.classList.contains(
        'audio-player__transcript-button'
      )
    ) {
      transcriptBtn.current.classList.remove('audio-player__transcript-button');
      transcriptWrapper?.current?.classList.remove(
        'audio-player__transcript-active'
      );
      transcriptWrapper?.current?.classList.add(
        'audio-player__transcript-transitioning'
      );
      setTimeout(() => {
        transcriptWrapper?.current?.classList.remove(
          'audio-player__transcript-transitioning'
        );
      }, 600);
    } else {
      transcriptBtn?.current?.classList.add('audio-player__transcript-button');
      transcriptWrapper?.current?.classList.add(
        'audio-player__transcript-active'
      );
    }
  };

  React.useEffect(() => {
    if (audioFilePath && player && player?.current?.audio?.current.duration) {
      setDuration(formatTime(player?.current?.audio?.current.duration));
    }

    if (transcript || transcriptDownloadFile?.mediaItemUrl) {
      player?.current?.container?.current?.classList?.add(
        'audio-player__has-transcript'
      );
    }
  }, [audioFilePath, audioFile, transcript, transcriptDownloadFile?.mediaItemUrl, player]);

  const playIcon = (
    <svg
      width="100%"
      height="100%"
      viewBox="0 0 54 54"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g clipPath="url(#play)">
        <path
          d="M27 54C41.9117 54 54 41.9117 54 27C54 12.0883 41.9117 0 27 0C12.0883 0 0 12.0883 0 27C0 41.9117 12.0883 54 27 54Z"
          fill="#2D8EC7"
        />
        <path
          d="M35.6857 28.8974L22.4268 36.8845C21.2365 37.5804 19.7373 36.6868 19.7373 35.2238V18.7752C19.7373 17.3201 21.2289 16.4186 22.4268 17.1145L35.6857 25.5761C36.9212 26.3036 36.9212 28.1778 35.6857 28.8974Z"
          fill="white"
        />
      </g>
      <defs>
        <clipPath id="play">
          <rect width="54" height="54" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );

  const pauseIcon = (
    <svg
      width="100%"
      height="100%"
      viewBox="0 0 54 54"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g clipPath="url(#pause)">
        <path
          d="M52.5 27C52.5 41.0833 41.0833 52.5 27 52.5C12.9167 52.5 1.5 41.0833 1.5 27C1.5 12.9167 12.9167 1.5 27 1.5C41.0833 1.5 52.5 12.9167 52.5 27Z"
          stroke="#101F5B"
          strokeWidth="3"
        />
        <path
          d="M21.8947 16H20.1053C18.9426 16 18 16.864 18 17.9298V36.0702C18 37.136 18.9426 38 20.1053 38H21.8947C23.0574 38 24 37.136 24 36.0702V17.9298C24 16.864 23.0574 16 21.8947 16Z"
          fill="#101F5B"
        />
        <path
          d="M33.8947 16H32.1053C30.9426 16 30 16.864 30 17.9298V36.0702C30 37.136 30.9426 38 32.1053 38H33.8947C35.0574 38 36 37.136 36 36.0702V17.9298C36 16.864 35.0574 16 33.8947 16Z"
          fill="#101F5B"
        />
      </g>
      <defs>
        <clipPath id="pause">
          <rect width="54" height="54" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );

  const volumeIcon = (
    <svg
      width="100%"
      height="100%"
      viewBox="0 0 34 27"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g>
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M16.0824 1.32676C16.4347 1.49095 16.66 1.84445 16.66 2.23317V24.8998C16.66 25.289 16.4342 25.6429 16.0812 25.8068C15.7282 25.9707 15.3121 25.915 15.0148 25.6638L8.04756 19.7798H3.89331L3.89033 19.7798C3.03448 19.7773 2.21453 19.4355 1.61024 18.8294C1.00596 18.2233 0.666622 17.4024 0.666626 16.5465V10.5465L0.66663 10.5435C0.669176 9.68931 1.00966 8.87084 1.61365 8.26684C2.21765 7.66285 3.03612 7.32237 3.89033 7.31982L3.89331 7.31981L8.04909 7.31982L15.0168 1.46743C15.3145 1.21743 15.7301 1.16256 16.0824 1.32676ZM14.66 4.37903L9.05649 9.08555C8.87633 9.23687 8.6486 9.31982 8.41333 9.31982H3.89502C3.56967 9.32112 3.25797 9.45095 3.02787 9.68106C2.79776 9.91117 2.66793 10.2229 2.66663 10.5482V16.5465C2.66662 16.873 2.79606 17.1861 3.02656 17.4173C3.25672 17.6481 3.56891 17.7785 3.89487 17.7798H8.41333C8.64952 17.7798 8.8781 17.8634 9.05855 18.0158L14.66 22.7464V4.37903Z"
          fill="#101F5B"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M19.5334 10.2065C19.5334 9.65426 19.9812 9.20654 20.5334 9.20654C21.6827 9.20654 22.7849 9.66307 23.5976 10.4757C24.4102 11.2884 24.8668 12.3906 24.8668 13.5399C24.8668 14.6892 24.4102 15.7913 23.5976 16.604C22.7849 17.4167 21.6827 17.8732 20.5334 17.8732C19.9812 17.8732 19.5334 17.4255 19.5334 16.8732C19.5334 16.3209 19.9812 15.8732 20.5334 15.8732C21.1523 15.8732 21.7458 15.6274 22.1834 15.1898C22.6209 14.7522 22.8668 14.1587 22.8668 13.5399C22.8668 12.921 22.6209 12.3275 22.1834 11.8899C21.7458 11.4524 21.1523 11.2065 20.5334 11.2065C19.9812 11.2065 19.5334 10.7588 19.5334 10.2065Z"
          fill="#101F5B"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M19.5334 5.8999C19.5334 5.34762 19.9812 4.8999 20.5334 4.8999C22.8249 4.8999 25.0225 5.81017 26.6428 7.43049C28.2631 9.0508 29.1734 11.2484 29.1734 13.5399C29.1734 15.8314 28.2631 18.029 26.6428 19.6493C25.0225 21.2696 22.8249 22.1799 20.5334 22.1799C19.9812 22.1799 19.5334 21.7322 19.5334 21.1799C19.5334 20.6276 19.9812 20.1799 20.5334 20.1799C22.2945 20.1799 23.9834 19.4803 25.2286 18.2351C26.4738 16.9898 27.1734 15.3009 27.1734 13.5399C27.1734 11.7789 26.4738 10.0899 25.2286 8.8447C23.9834 7.59946 22.2945 6.8999 20.5334 6.8999C19.9812 6.8999 19.5334 6.45219 19.5334 5.8999Z"
          fill="#101F5B"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M24.8275 3.35096C23.4688 2.81615 22.0168 2.55842 20.5571 2.5929C20.0049 2.60594 19.5468 2.16892 19.5337 1.61679C19.5207 1.06466 19.9577 0.606498 20.5098 0.593456C22.2362 0.552679 23.9532 0.857478 25.56 1.48994C27.1668 2.12239 28.6309 3.06974 29.8663 4.27627C31.1017 5.4828 32.0833 6.92417 32.7536 8.51559C33.4238 10.107 33.769 11.8164 33.769 13.5432C33.769 15.27 33.4238 16.9794 32.7536 18.5708C32.0833 20.1622 31.1017 21.6036 29.8663 22.8101C28.6309 24.0166 27.1668 24.964 25.56 25.5964C23.9532 26.2289 22.2362 26.5337 20.5098 26.4929C19.9577 26.4799 19.5207 26.0217 19.5337 25.4696C19.5468 24.9174 20.0049 24.4804 20.5571 24.4935C22.0168 24.5279 23.4688 24.2702 24.8275 23.7354C26.1862 23.2006 27.4243 22.3995 28.4689 21.3793C29.5135 20.359 30.3436 19.1402 30.9104 17.7945C31.4771 16.4488 31.769 15.0034 31.769 13.5432C31.769 12.083 31.4771 10.6376 30.9104 9.29186C30.3436 7.94614 29.5135 6.72732 28.4689 5.70708C27.4243 4.68685 26.1862 3.88577 24.8275 3.35096Z"
          fill="#101F5B"
        />
      </g>
    </svg>
  );

  const muteIcon = (
    <svg
      width="100%"
      height="100%"
      viewBox="0 0 34 26"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g clipPath="url(#mute)">
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M15.7917 0.494295C16.1526 0.66249 16.3834 1.02461 16.3834 1.42281V24.6423C16.3834 25.0409 16.1521 25.4035 15.7905 25.5714C15.4289 25.7393 15.0027 25.6822 14.6981 25.4249L7.56095 19.3974H3.30538H3.30233C2.4256 19.3948 1.58566 19.0447 0.966628 18.4238C0.34761 17.8029 -4.09752e-06 16.962 0 16.0852V9.93889L4.152e-06 9.93582C0.00261225 9.0608 0.3514 8.22237 0.970121 7.60364C1.58885 6.98491 2.42728 6.63613 3.30233 6.63352L3.30538 6.63351L7.56251 6.63352L14.7002 0.638396C15.0051 0.382299 15.4309 0.326091 15.7917 0.494295ZM14.3347 3.62101L8.59448 8.44231C8.40993 8.59732 8.17664 8.6823 7.93564 8.6823H3.30713C2.97385 8.68363 2.65454 8.81662 2.41883 9.05235C2.18311 9.28807 2.05011 9.6074 2.04878 9.94064V16.0852C2.04877 16.4197 2.18137 16.7404 2.41749 16.9773C2.65326 17.2137 2.97307 17.3473 3.30698 17.3486H7.93564C8.17759 17.3486 8.41174 17.4342 8.59659 17.5904L14.3347 22.4363V3.62101Z"
          fill="#101F5B"
        />
        <path
          d="M28 8.35468L19.8049 17.7205"
          stroke="#101F5B"
          strokeWidth="2"
          strokeLinecap="round"
        />
        <path
          d="M19.8049 8.35468L28 17.7205"
          stroke="#101F5B"
          strokeWidth="2"
          strokeLinecap="round"
        />
      </g>
      <defs>
        <clipPath id="mute">
          <rect width="33.1" height="25.91" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );

  return (
    <section id={anchorTagLinkText && `${toSnakeCase(anchorTagLinkText)}`} className="audio-player-module__wrapper">
      <div className="ui container">
        <div className="grid-wrapper">
          <div className="audioPlayerWrapper">
            {heading || transcript || introText ? (
              <div className="contentWrapper">
                {heading || introText ? (
                  <div className="headingTextGroup">
                    {heading ? (
                      <div className="heading">
                        <h2 className="heading-two">{heading}</h2>
                      </div>
                    ) : null}
                    {introText ? (
                      <div className="introTextWrapper">
                        <p>{introText}</p>
                      </div>
                    ) : null}
                  </div>
                ) : null}
                <div ref={transcriptWrapper} className="transcriptWrapper">
                  {transcript ? (
                    <div>
                        {/* eslint-disable-next-line */}
                      <div role="region" aria-label="Scrollable Content" tabIndex={0} className="transcriptContent">
                        <div
                          dangerouslySetInnerHTML={{ __html: transcript }}
                        ></div>
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            ) : null}

            <div className="playerWrapper">
              <AudioPlayer
                ref={player}
                src={audioFilePath}
                showFilledVolume={true}
                showJumpControls={false}
                defaultCurrentTime={`00:00`}
                customAdditionalControls={[]}
                customIcons={{
                  play: playIcon,
                  pause: pauseIcon,
                  volume: volumeIcon,
                  volumeMute: muteIcon,
                }}
              />
              {duration ? (
                <div
                  className="durationWrapper"
                  aria-description="duration of audio playback"
                >
                  {duration}
                </div>
              ) : null}

              {transcript || transcriptDownloadFile ? (
                <div className="customButtonsWrapper">
                  {transcript ? (
                    <div className="toggleTranscriptWrapper">
                      <button
                        tabIndex={0}
                        onClick={showTranscript}
                        ref={transcriptBtn}
                        className="customButton transcriptButton"
                      >
                        <span>Transcript</span>
                      </button>
                    </div>
                  ) : null}
                  {transcriptDownloadFile ? (
                    <div className="downloadWrapper">
                      <a
                        tabIndex={0}
                        target="_blank"
                        rel="noreferrer"
                        download
                        href={transcriptDownloadFile?.mediaItemUrl}
                        className="customButton downloadButton"
                      >
                        <span>Download</span>
                        <DownloadIcon />
                      </a>
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AudioPlayerModule;
